import { useState, type ReactNode, type ReactElement } from 'react';
import type { Abweichung } from '@schnitt/shared';

export function Sheet({
  title,
  onClose,
  children,
  actions,
  /** A sheet opened from another sheet goes back to it, so it says so. */
  closeLabel = 'Fertig',
  /** Über den ganzen Schirm -- für das, was Platz braucht (der Rekorder, 15. Oktober). */
  voll = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  closeLabel?: string;
  voll?: boolean;
}): ReactElement {
  // 7.18: Das Blatt geht dorthin zurück, wo es herkam -- nach unten --, statt
  // zu verschwinden. 160 ms, und wer keine Bewegung will
  // (prefers-reduced-motion), bekommt sie nicht: dann schliesst es sofort.
  const [geht, setGeht] = useState(false);
  const schliesse = () => {
    if (geht) return;
    if (ohneBewegung()) {
      onClose();
      return;
    }
    setGeht(true);
    window.setTimeout(onClose, 160);
  };
  return (
    <>
      <div className={`sheet-backdrop ${geht ? 'geht' : ''}`} onClick={schliesse} />
      <div className={`sheet ${voll ? 'voll' : ''} ${geht ? 'geht' : ''}`} role="dialog" aria-label={title} aria-modal="true">
        <div className="grab" />
        <div className="sheet-header">
          <h2>{title}</h2>
          {actions}
          <button className="ghost small" onClick={schliesse} aria-label={closeLabel}>
            {closeLabel}
          </button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </>
  );
}

/** Der grosse Schirm: dort ist das Blatt eine Spalte, und Gruppen stehen unter Reitern (7.25b). */
export function amSchreibtisch(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches === true;
}

/** Will das Gerät keine Bewegung? Dann bewegt sich hier nichts. */
export function ohneBewegung(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 0.01,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  format?: (v: number) => string;
  onChange: (value: number) => void;
}): ReactElement {
  return (
    <div className="slider-row">
      <label>{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="value">{format ? format(value) : value.toFixed(2)}</span>
    </div>
  );
}


/**
 * Eine Gruppe im Einstellblatt: Überschrift, und darunter das Feine (7.15).
 *
 * „Das Blatt ist eine Liste von Reglern, die man von oben nach unten
 * durchgehen muss“ (Sascha, 4. September). Also klappt zu, was auf Standard
 * steht -- und offen steht, was angefasst wurde. Die Überschrift sagt beides:
 * *dass* etwas geändert ist und *was*, damit man nicht aufklappen muss, um es
 * zu erfahren.
 *
 * Der Anfangszustand kommt aus den Abweichungen und nicht aus einer Erinnerung
 * an das letzte Mal: wer einen Clip aufmacht, will sehen, was an ihm anders
 * ist -- nicht, was er beim vorigen Clip aufgeklappt hatte.
 */
export function Gruppe({
  titel,
  geaendert = [],
  onZuruecksetzen,
  bleibtZu = false,
  reiter,
  children,
}: {
  titel: string;
  /** Was in dieser Gruppe vom Standard abweicht. */
  geaendert?: Abweichung[];
  /** Fehlt er, gibt es kein Zurücksetzen -- etwa bei Gruppen ohne Standard. */
  onZuruecksetzen?: () => void;
  /**
   * Von selbst aufklappen, nur weil hier etwas steht? Für die Keyframes nicht
   * mehr: dort *ist* immer etwas, sobald man einmal einen gesetzt hat, und
   * dann stand das Blatt jedes Mal offen (Sascha, 8. September: „das
   * Keyframes-Menü ausklappen nur weil welche vorhanden sind, brauchen wir
   * nicht mehr“). Die Zahl an der Überschrift bleibt.
   */
  bleibtZu?: boolean;
  /** Zu welchem Reiter die Gruppe am grossen Schirm gehört (7.25b): bild, bewegung, farbe, ton. */
  reiter?: string;
  children: ReactNode;
}): ReactElement {
  // Am grossen Schirm stehen die Gruppen unter Reitern, und ein Reiter zeigt
  // wenige Gruppen -- die dürfen offen stehen. Auf dem Telefon bleibt zu, was
  // auf Standard steht (7.15).
  const [offen, setOffen] = useState(!bleibtZu && (geaendert.length > 0 || amSchreibtisch()));
  return (
    <section className={`einstell-gruppe ${offen ? 'offen' : ''}`} data-gruppe={titel} data-geaendert={geaendert.length} data-reiter={reiter}>
      <div className="gruppe-kopf">
        <button className="gruppe-titel" aria-expanded={offen} onClick={() => setOffen(!offen)}>
          <span className="titel">{titel}</span>
          {geaendert.length ? <span className="zaehler">{geaendert.length}</span> : null}
          <span className="pfeil" aria-hidden="true">{offen ? '▴' : '▾'}</span>
        </button>
        {geaendert.length && onZuruecksetzen ? (
          <button
            className="ghost small"
            data-zuruecksetzen
            title={`${titel} auf Standard zurücksetzen`}
            onClick={onZuruecksetzen}
          >
            Zurücksetzen
          </button>
        ) : null}
      </div>
      {/* Zugeklappt steht trotzdem da, *was* anders ist -- sonst müsste man
          jede Gruppe einmal aufmachen, um den Clip zu verstehen. */}
      {!offen && geaendert.length ? (
        <p className="hint gruppe-kurz" style={{ margin: '0 0 6px' }}>
          {geaendert.map((a) => `${a.label} ${a.wert}`).join(' · ')}
        </p>
      ) : null}
      {offen ? <div className="gruppe-inhalt">{children}</div> : null}
    </section>
  );
}
